import React from 'react';
import { Alert, Box, Button, Container, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { useUser } from '../Components/useUser';
import {
  getAuthorizationFailureReason,
  getDefaultAuthorizedPath,
} from '../auth/authorization';

const REASON_COPY = {
  already_authorized: {
    title: 'You already have internal access',
    message: 'Your account is authorized. Use the button below to return to an allowed area.',
  },
  missing_profile: {
    title: 'No internal profile found',
    message: 'Your account is authenticated, but no internal access profile is assigned yet.',
  },
  pending: {
    title: 'Access pending approval',
    message: 'Your account is waiting for an administrator to approve internal access.',
  },
  disabled: {
    title: 'Access disabled',
    message: 'Your internal access has been disabled. Contact an administrator if this looks incorrect.',
  },
  role_not_allowed: {
    title: 'Insufficient role for this page',
    message: 'Your account is signed in, but your current role does not allow access to this route.',
  },
  unknown_role: {
    title: 'Unrecognized role',
    message: 'Your profile role is not recognized by the frontend authorization layer.',
  },
  default: {
    title: 'Unauthorized',
    message: 'You do not have access to this page.',
  },
};

const UnauthorizedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, loading } = useUser();

  if (loading) return null;

  const fallbackPath = getDefaultAuthorizedPath(profile?.role);
  const currentReason = getAuthorizationFailureReason(profile, []);
  const routeScopedReason = location.state?.reason === 'role_not_allowed'
    ? 'role_not_allowed'
    : null;
  const effectiveReason = currentReason || routeScopedReason || (fallbackPath ? 'already_authorized' : 'default');
  const copy = REASON_COPY[effectiveReason] || REASON_COPY.default;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <Container maxWidth="sm" sx={{ py: 10 }}>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 3, sm: 4 }, boxShadow: 2 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {copy.title}
            </Typography>
            <Typography color="text.secondary">
              {copy.message}
            </Typography>
          </Box>

          {user ? (
            <Alert severity="info">
              Signed in as {user.email || 'an authenticated user'}
              {profile?.role ? ` with role "${profile.role}".` : '.'}
            </Alert>
          ) : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {fallbackPath ? (
              <Button variant="contained" onClick={() => navigate(fallbackPath, { replace: true })}>
                {`Go to ${fallbackPath}`}
              </Button>
            ) : null}
            <Button variant={user ? 'outlined' : 'contained'} onClick={() => navigate('/login', { replace: true })}>
              Go to login
            </Button>
            {user ? (
              <Button color="inherit" onClick={handleLogout}>
                Log out
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};

export default UnauthorizedPage;
